import { horizontalScatter } from "../options/horizontal-scatter";
import getEmphasis from "./get-emphasis";
import { lengthDataHandler } from "./get-length";
import { groupArrays } from "./group-arrays";
import {
  handleLabelFormatting,
  handleTooltipFormatting,
} from "./handle-formatting";
import { getTextStyle } from "./text-style";

type TProps = {
  primaryColor?: string;
  colors: string[];
  type: string;
  data: Record<string, string>[][];
  payload: Record<string, unknown>;
};

export const scatterData = ({
  data,
  colors,
  primaryColor,
  type,
  payload,
}: TProps) => {
  const annotationTextStyle = getTextStyle(payload, "annotation");
  const [seriesTitles = [], ...restData] = data;
  seriesTitles.shift();
  const grouped = groupArrays(
    restData.map((row) => row.filter((cell) => !!cell?.value)),
  );
  let [axisTitles = [], ...gropedData] = grouped;
  const titles = axisTitles.map((cell) => cell?.value ?? "");

  if (type === "horizontal-scatter") {
    titles.reverse();
    gropedData = gropedData.map((arr) => arr.reverse());

    if (gropedData.length === 1) {
      colors = colors.splice(0, gropedData[0].length);
      if (colors.length < gropedData[0].length) {
        colors = colors.concat(
          Array(gropedData[0].length - colors.length).fill(primaryColor),
        );
      }
      colors.reverse();
    }
  }

  let series = gropedData.map((arr, i) => {
    const mapped = arr.map((cell) => cell?.value ?? 0);
    const seriesColor = colors[i] ?? primaryColor;
    return {
      data: mapped,
      type: "scatter",
      itemStyle: { color: seriesColor },
      name: seriesTitles[i]?.value || "",
      // universalTransition: true,
      animationDurationUpdate: 1000,
      label: {
        ...seriesLabel({ payload, type }),
        ...annotationTextStyle,
        color:
          payload.annotationPosition !== "inside"
            ? annotationTextStyle.color
            : undefined,
      },
      symbolSize: payload.symbolSize ?? 60,
      tooltip: (params: Record<string, string | number>) =>
        handleTooltipFormatting(params, payload.prefix, payload.suffix),
      emphasis: getEmphasis(seriesColor),
    };
  });

  if (series.length === 1) {
    const seriesData = series[0].data.map(
      (value: number | string, index: number) => {
        const itemColor = colors[index] || primaryColor;
        return {
          value: value,
          itemStyle: { color: itemColor },
          emphasis: getEmphasis(itemColor || ""),
        };
      },
    );
    series = [
      {
        data: seriesData,
        type: "scatter",
        name: seriesTitles[0]?.value || "",
        // universalTransition: true,
        animationDurationUpdate: 1000,
        label: {
          ...seriesLabel({ payload, type }),
          ...annotationTextStyle,
          color:
            payload.annotationPosition !== "inside"
              ? annotationTextStyle.color
              : undefined,
        },
        symbolSize: payload.symbolSize ?? 60,
        tooltip: (params: Record<string, string | number>) =>
          handleTooltipFormatting(params, payload.prefix, payload.suffix),
      },
    ] as any[];
  }
  return {
    option: horizontalScatter(payload, { titles, series }),
    ...lengthDataHandler(titles, series),
  };
};

const seriesLabel = ({
  payload,
  type,
}: {
  payload: Record<string, unknown>;
  type: string;
}) => {
  const { showAnnotation, annotationPosition, color } = payload;
  const outSlidePosition = type === "horizontal-stacked" ? "right" : "top";

  const label: Record<string, unknown> = {
    show: showAnnotation,
    position: annotationPosition === "inside" ? "inside" : outSlidePosition,
    formatter: (params: Record<string, string | number>) =>
      handleLabelFormatting(params, payload.prefix, payload.suffix),
    fontWeight: annotationPosition === "inside" ? 700 : "normal",
  };
  if (annotationPosition !== "inside") label["color"] = color;
  return label;
};
